import type { TokenId } from "@/types";
import { useTransactionFees } from "@/hooks";
import { mintEventEmitter, MINT_EVENTS } from "@/events";
import { gatewayMintTransaction, estimateMintTransactionGas, prepareMintTransaction } from "@/services";
import { useGasEstimation } from "@/hooks";
import { useTransactionExecution } from "@/hooks";
import { useTokenApprovalV2 } from "./useTokenApprovalV2";
import { mainnet } from "viem/chains";
import { getGatewayContractAddress } from "@/config";

interface MintTransactionProps {
    mintAmount: number;
    mintTokenId: TokenId;
}

export function useMintTransaction({ mintAmount, mintTokenId }: MintTransactionProps) {
    const { isApproved, isApproving } = useTokenApprovalV2({
        tokenId: mintTokenId,
        amount: mintAmount,
        spender: getGatewayContractAddress(mainnet.id),
    });

    const {
        gasEstimate,
        isEstimatingGas,
        error: gasError,
    } = useGasEstimation({
        estimateGasFn: (walletClient) => estimateMintTransactionGas(mintAmount, walletClient),
        prepareTransactionFn: async (walletClient) => {
            return prepareMintTransaction(mintAmount, walletClient);
        },
        hasApproval: isApproved,
        tokenId: mintTokenId,
    });

    const { gasPriceUSD, slippagePercent } = useTransactionFees({
        estimatedGas: gasEstimate,
    });

    const {
        transactionHash,
        isExecuting,
        error: transactionError,
        executeTransaction: executeMintTransaction,
    } = useTransactionExecution({
        executeTransactionFn: (walletClient) => gatewayMintTransaction(mintAmount, walletClient),
        onSuccess: (hash) => {
            mintEventEmitter.emit(MINT_EVENTS.SUCCESS, {
                transactionHash: hash,
                mintTokenId,
                mintAmount,
            });
        },
        errorMessage: "Failed to mint tokens",
    });

    return {
        gasPriceUSD,
        slippagePercent,
        transactionHash,
        executeMintTransaction,
        isLoading: isExecuting || isApproving,
        isEstimatingGas,
        needsApproval: !isApproved,
        error: gasError || transactionError,
        gasEstimate,
        isExecuting,
    };
}
